import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Award, Download, ExternalLink, Calendar, CheckCircle2,
  FileText, Shield, Star, Clock, GraduationCap, Search, RefreshCw
} from 'lucide-react';

export default function MyCertificates() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCertificates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/certificates/my');
      setCertificates(response.data.certificates || []);
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
      // Show empty state if endpoint not available
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const handleDownload = async (cert) => {
    if (!cert.certificate_number) {
      toast.error('Certificate ID not available');
      return;
    }

    setDownloading(prev => ({ ...prev, [cert.id]: true }));
    
    try {
      const response = await api.get(`/certificates/download/${cert.certificate_number}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${cert.certificate_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded successfully');
    } catch (error) {
      console.error('Failed to download certificate:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(prev => ({ ...prev, [cert.id]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Compliance': 'bg-blue-100 text-blue-700 border-blue-200',
      'Safety': 'bg-green-100 text-green-700 border-green-200',
      'Clinical': 'bg-purple-100 text-purple-700 border-purple-200',
      'Ethics': 'bg-amber-100 text-amber-700 border-amber-200',
      'Technical': 'bg-cyan-100 text-cyan-700 border-cyan-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const filteredCertificates = certificates.filter(cert =>
    cert.course_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cert.course_category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="my-certificates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            My Certificates
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            View and download your training completion certificates
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchCertificates}
          className="gap-2"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Search */}
      {certificates.length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search certificates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            data-testid="certificate-search"
          />
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-3 text-gray-500">Loading certificates...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && certificates.length === 0 && (
        <Card className="card-premium p-12 text-center">
          <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Certificates Yet</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Complete your assigned training courses to earn certificates. 
            Your certificates will appear here once you pass the required assessments.
          </p>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white gap-2">
            <GraduationCap className="w-4 h-4" />
            View Available Courses
          </Button>
        </Card>
      )}

      {/* Certificates Grid */}
      {!loading && filteredCertificates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredCertificates.map((cert) => (
            <Card 
              key={cert.id} 
              className="card-premium overflow-hidden hover:shadow-lg transition-all duration-300"
              data-testid={`certificate-card-${cert.id}`}
            >
              {/* Certificate Header - Decorative */}
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 px-5 py-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full transform translate-x-8 -translate-y-8"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Award className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white/80 text-xs">Certificate of Completion</p>
                      <p className="text-white font-semibold text-sm">{cert.certificate_number || 'PENDING'}</p>
                    </div>
                  </div>
                  {cert.score && (
                    <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full">
                      <Star className="w-3.5 h-3.5 text-yellow-300" />
                      <span className="text-white text-sm font-medium">{cert.score}%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Certificate Content */}
              <div className="p-5">
                <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                  {cert.course_title || 'Training Course'}
                </h3>
                
                {cert.course_category && (
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border mb-3 ${getCategoryColor(cert.course_category)}`}>
                    {cert.course_category}
                  </span>
                )}

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Issued: {formatDate(cert.issued_at)}</span>
                  </div>
                  
                  {cert.expires_at && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>Expires: {formatDate(cert.expires_at)}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="font-medium">Completed</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(cert)}
                    disabled={downloading[cert.id]}
                    className="flex-1 gap-2"
                    data-testid={`download-cert-${cert.id}`}
                  >
                    {downloading[cert.id] ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Download PDF
                      </>
                    )}
                  </Button>
                  
                  {cert.pdf_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(cert.pdf_url, '_blank')}
                      className="text-purple-600"
                      title="View online"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Verification Badge */}
              <div className="px-5 pb-4">
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                  <Shield className="w-3.5 h-3.5 text-green-500" />
                  <span>Verified • ID: {cert.certificate_number || 'Pending'}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* No Results */}
      {!loading && certificates.length > 0 && filteredCertificates.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No certificates match your search</p>
        </div>
      )}
    </div>
  );
}
